import React from 'react'
import styles from './OrganizationsSearch.scss'
import Helmet from 'react-helmet'
import {Card} from 'common/styleguide/Card'
import {SearchableCardMulti, SearchableCardInput, HybridBrowserLocationInput} from 'common/SearchableCardMulti'
import cardStyles from 'common/SearchableCard/SearchableCard.scss'
import {deserializeOrganization} from 'lib/api/records'
import fetchJSON from 'lib/fetchJSON'
import {OrgListItem} from 'common/ListItems'
import {findAsYouType} from 'lib/findAsYouType'
import {Loadable} from 'common/Loadable'
import {CTA} from 'common/styleguide/CTA'
import {CircleSpinner} from 'common/spinners/CircleSpinner'
import queryString from 'query-string'

const PER_PAGE = 100

const orgSearch = ({keywords, location, lat, lng, offset} = {}) => {
  const staticParams = {apikey: 'x', version: 3, per_page: PER_PAGE}
  const searchParams = {
    // account for the backend returning garbage for location/keywords < 3 chars
    keywords: keywords && keywords.length >= 3 ? keywords : undefined,
    location: location && location.length >= 3 ? location : undefined,
    lat,
    lng,
    org_type: 'club',
    offset
  }
  const params = Object.entries(searchParams)
    .filter(([k, v]) => v)
    .map(([k, v]) => [`search[${k}]`, v])
    .reduce((o, [k, v]) => { o[k] = v; return o }, staticParams)

  const qs = queryString.stringify(params)
  return fetchJSON(`/organizations/search.json?${qs}`)
    .then(({results, results_count: resultsCount}) => ({
      results: results.map(deserializeOrganization),
      resultsCount: resultsCount
    }))
}

class _OrganizationsSearch extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      search: props.initialSearch,
      keywords: '',
      location: '',
      lat: null,
      lng: null,
      loadMorePending: false
    }
    this._search = findAsYouType({
      searchFn: this.executeSearch,
      onResults: (results) => this.setState({search: results})
    })
  }

  executeSearch = () => {
    const {keywords, location, lat, lng} = this.state
    return orgSearch({keywords, location, lat, lng})
  }

  loadMore = () => {
    const {keywords, location, lat, lng, search} = this.state
    this.setState({loadMorePending: true})
    orgSearch({keywords, location, lat, lng, offset: search.results.length})
      .then(newSearch => {
        this.setState({
          loadMorePending: false,
          search: {
            ...this.state.search,
            results: this.state.search.results.concat(newSearch.results)
          }
        })
      })
  }

  onSearchKeywordChange = (e) => {
    const keywords = e.target.value
    this.setState({keywords}, this._search)
  }

  onSearchLocationChange = ({location, lat, lng}) => {
    if (lat && lng) {
      this.setState({location: '', lat, lng}, this._search)
    } else {
      this.setState({location, lat: null, lng: null}, this._search)
    }
  }

  render () {
    const {keywords, location, lng, lat, search, loadMorePending} = this.state

    return (
      <div className={styles.OrganizationsSearch}>
        <h2 data-heading-alt>Club Directory</h2>
        <Card noPadding>
          <Helmet title='Club Search · Ride with GPS' />
          <SearchableCardMulti
            resultsLength={search.results.length}
            resultsCount={search.resultsCount}
            inputs={[
              <SearchableCardInput
                key='keyword'
                placeholder='Keyword'
                onKeywordChange={this.onSearchKeywordChange}
                keywords={keywords} />,
              <HybridBrowserLocationInput
                key='location'
                onLocationInputChange={this.onSearchLocationChange}
                lng={lng}
                lat={lat}
                location={location} />
            ]}>
            {search.results.map(org => <OrgListItem key={org.id} org={org} />)}
            <div className={cardStyles.resultsFooter}>
              {search.resultsCount > search.results.length &&
                <CTA inline secondary margins={false} disabled={loadMorePending} onClick={() => !loadMorePending && this.loadMore()}>
                  {loadMorePending && <CircleSpinner size={14} />} Load more
                </CTA>}
              <div style={{paddingTop: '20px'}}>
                <span className={cardStyles.footerText}>Showing 1 - {search.results.length} of {search.resultsCount} results</span>
              </div>
            </div>
          </SearchableCardMulti>
        </Card>
      </div>
    )
  }
}

export const OrganizationsSearch = () => (
  <Loadable
    component={({loadableResources}) => <_OrganizationsSearch initialSearch={loadableResources} />}
    loadingText='Loading Organizations'
    resourceLoaded={() => orgSearch({keywords: ''})} />
)
